import React, { useState, useEffect } from "react";
import styles from "./ClinicDesktopPage.module.scss";
import ProductListRow from "@src/components/widgets/ProductListRow";
import PxProductTile from "@src/components/widgets/PxProductTile/PxProductTile";
import SectionHeader from "@src/components/widgets/SectionHeader/SectionHeader";
import DistributorLogo from "@src/components/common/DistributorLogo/DistributorLogo";
import HorizontalScrollRow from "@src/components/common/HorizontalScrollRow/HorizontalScrollRow";
import { Link } from "react-router-dom";
import * as ProductApi from "@src/api/ProductApi";
import SpinningLoader from "@src/components/common/SpinningLoader/SpinningLoader";
import { useToast } from "@swordsweeper/ssi-ui-component-library";
import kebabCase from "lodash/kebabCase";
import { useFavoriteProductsStore } from "@src/state/favoriteProductsState";
import ProductRecommendationTypes from "@src/components/widgets/ProductRecommendations/ProductRecommendationTypes";

const MAX_VISIBLE_PER_ROW = 15;
const PREVIOUSLY_PURCHASED_FETCH_SIZE = MAX_VISIBLE_PER_ROW + 1;

function ViewAllCard({ to }: { to: string }) {
  return (
    <Link to={to} className={styles.viewAllCard}>
      View All
    </Link>
  );
}

interface Product {
  id: string;
  [key: string]: any;
}

interface ProductCategory {
  id: string;
  name: string;
  firstFiveProducts: Product[];
}

interface PromotedProductGroup {
  distributorId: string;
  distributorName: string;
  distributorSlug: string;
  promotedProducts: Product[];
}

function ClinicDesktopPage() {
  const { showToast } = useToast();
  const [landingPageProductCategories, setLandingPageProductCategories] =
    useState<ProductCategory[]>([]);
  const [promotedProductGroups, setPromotedProductGroups] = useState<
    PromotedProductGroup[]
  >([]);
  const [previouslyPurchasedProducts, setPreviouslyPurchasedProducts] =
    useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { favorites, fetchFavorites } = useFavoriteProductsStore();

  useEffect(() => {
    loadLandingPageData();
  }, []);

  const loadLandingPageData = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const [landingResponse, ppResponse] = await Promise.all([
        ProductApi.getLandingPageProducts(),
        ProductApi.getProductRecommendations(
          ProductRecommendationTypes.PastPurchases,
          { pageSize: PREVIOUSLY_PURCHASED_FETCH_SIZE },
        ),
        fetchFavorites(),
      ]);
      setLandingPageProductCategories(landingResponse.body.categories);
      setPromotedProductGroups(landingResponse.body.promotedProducts || []);
      setPreviouslyPurchasedProducts(ppResponse.body.results ?? []);
    } catch (e) {
      console.error(e);
      showToast({
        message: "Error loading products",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const hasFavorites = !!favorites && favorites.length > 0;

  return (
    <div className="spaced-content-v-lg">
      {isLoading && <SpinningLoader />}
      {hasFavorites && (
        <>
          <SectionHeader
            header={<span className={styles.sectionHeading}>Favorites</span>}
            rightSideContent={
              <Link className="text-xs" to="/favorites">
                View All
              </Link>
            }
          />
          <HorizontalScrollRow>
            {favorites!.slice(0, MAX_VISIBLE_PER_ROW).map((fav) => (
              <PxProductTile
                key={`favorites-tile-${fav.product.id}`}
                product={fav.product}
                imageSizePx={180}
                className="height-initial-important width-100-important"
              />
            ))}
            {favorites!.length > MAX_VISIBLE_PER_ROW && (
              <ViewAllCard to="/favorites" />
            )}
          </HorizontalScrollRow>
        </>
      )}

      {previouslyPurchasedProducts.length > 0 && (
        <>
          <SectionHeader
            header={
              <span className={styles.sectionHeading}>
                Previously Purchased
              </span>
            }
            rightSideContent={
              <Link
                className="text-xs"
                to={`/product-recommendations/${ProductRecommendationTypes.PastPurchases}`}
              >
                View All
              </Link>
            }
          />
          <HorizontalScrollRow>
            {previouslyPurchasedProducts
              .slice(0, MAX_VISIBLE_PER_ROW)
              .map((distributorProduct) => (
                <PxProductTile
                  key={`prev-purchased-tile-${distributorProduct.id}`}
                  product={distributorProduct.product}
                  imageSizePx={180}
                  className="height-initial-important width-100-important"
                />
              ))}
            {previouslyPurchasedProducts.length > MAX_VISIBLE_PER_ROW && (
              <ViewAllCard
                to={`/product-recommendations/${ProductRecommendationTypes.PastPurchases}`}
              />
            )}
          </HorizontalScrollRow>
        </>
      )}

      {promotedProductGroups.length > 0 && (
        <>
          <SectionHeader
            header={
              <span className={styles.sectionHeading}>Promoted Products</span>
            }
          />
          {promotedProductGroups.map((group) => (
            <React.Fragment key={`promoted-${group.distributorId}`}>
              <SectionHeader
                header={
                  <DistributorLogo
                    distributorSlug={group.distributorSlug}
                    height={30}
                    ariaLabel={`${group.distributorName} Promoted Products`}
                  />
                }
                rightSideContent={
                  <Link
                    className="text-xs"
                    to={`/promoted-products/${group.distributorSlug}`}
                  >
                    View All
                  </Link>
                }
              />
              <HorizontalScrollRow>
                {group.promotedProducts
                  .slice(0, MAX_VISIBLE_PER_ROW)
                  .map((product) => (
                    <PxProductTile
                      key={`promoted-tile-${product.id}`}
                      product={product}
                      imageSizePx={180}
                      className="height-initial-important width-100-important"
                    />
                  ))}
                {group.promotedProducts.length > MAX_VISIBLE_PER_ROW && (
                  <ViewAllCard
                    to={`/promoted-products/${group.distributorSlug}`}
                  />
                )}
              </HorizontalScrollRow>
            </React.Fragment>
          ))}
        </>
      )}

      {landingPageProductCategories.map((productCategory) => (
        <React.Fragment key={`product-category-${productCategory.id}`}>
          <SectionHeader
            header={productCategory.name}
            rightSideContent={
              <Link
                id={`view-all-products-link-${kebabCase(productCategory.name)}`}
                className="text-xs"
                to={`/product-category/${productCategory.id}`}
              >
                View All
              </Link>
            }
          />
          <ProductListRow
            products={productCategory.firstFiveProducts}
            tileClassName="height-initial-important"
            imageSizePx={180}
          />
        </React.Fragment>
      ))}
    </div>
  );
}

export default ClinicDesktopPage;
